import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";

export default class extends Controller {
  static targets = ["display", "callButton", "hangupButton", "muteButton", "status"];
  static values = {
    canMakeCall: Boolean,
    hasSubscription: Boolean,
    hasPhoneNumber: Boolean
  };

  connect() {
    this.phoneNumber = "";
    this.isInCall = false;
    this.isMuted = false;

    // Listen for Twilio call events on document (since events bubble from twilio-client element)
    this.boundHandleRinging = (e) => this.handleRinging(e);
    this.boundHandleAccepted = (e) => this.handleAccepted(e);
    this.boundHandleDisconnected = (e) => this.handleDisconnected(e);
    this.boundHandleError = (e) => this.handleError(e);

    // Twilio events
    document.addEventListener("twilio:call:ringing", this.boundHandleRinging);
    document.addEventListener("twilio:call:accepted", this.boundHandleAccepted);
    document.addEventListener("twilio:call:disconnected", this.boundHandleDisconnected);
    document.addEventListener("twilio:call:error", this.boundHandleError);

    // Telnyx events (same handlers, different event names)
    document.addEventListener("telnyx:call:ringing", this.boundHandleRinging);
    document.addEventListener("telnyx:call:accepted", this.boundHandleAccepted);
    document.addEventListener("telnyx:call:disconnected", this.boundHandleDisconnected);
    document.addEventListener("telnyx:call:error", this.boundHandleError);

    this.boundHandleKeydown = (event) => this.handleKeydown(event);
    document.addEventListener("keydown", this.boundHandleKeydown);
  }

  // Open the dial pad modal
  open(event) {
    if (event) {
      event.preventDefault();
    }
    const modal = document.getElementById("dialNumberModal");
    if (modal) {
      modal.classList.remove("hidden");
      document.body.classList.add("modal-scroll-locked");
    }
  }

  // Close the dial pad modal
  close(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const modal = document.getElementById("dialNumberModal");
    if (modal) {
      modal.classList.add("hidden");
      document.body.classList.remove("modal-scroll-locked");
    }
  }

  disconnect() {
    // Clean up Twilio event listeners
    if (this.boundHandleRinging) {
      document.removeEventListener("twilio:call:ringing", this.boundHandleRinging);
      document.removeEventListener("telnyx:call:ringing", this.boundHandleRinging);
    }
    if (this.boundHandleAccepted) {
      document.removeEventListener("twilio:call:accepted", this.boundHandleAccepted);
      document.removeEventListener("telnyx:call:accepted", this.boundHandleAccepted);
    }
    if (this.boundHandleDisconnected) {
      document.removeEventListener("twilio:call:disconnected", this.boundHandleDisconnected);
      document.removeEventListener("telnyx:call:disconnected", this.boundHandleDisconnected);
    }
    if (this.boundHandleError) {
      document.removeEventListener("twilio:call:error", this.boundHandleError);
      document.removeEventListener("telnyx:call:error", this.boundHandleError);
    }
    if (this.boundHandleKeydown) {
      document.removeEventListener("keydown", this.boundHandleKeydown);
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close(event);
    }
  }

  // Stop propagation on modal panel clicks (to prevent closing when clicking inside)
  stopPropagation(event) {
    event.stopPropagation();
  }

  // Digit button clicked
  digitPressed(event) {
    const digit = event.currentTarget.dataset.digit;

    if (this.isInCall) {
      // Send DTMF tone during call
      this.sendDTMF(digit);
    } else {
      // Add to display
      this.phoneNumber += digit;
      this.updateDisplay();
    }

    // Play beep sound
    this.playTone(digit);
  }

  // Backspace button
  backspace(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Always allow backspace when not in a call
    if (!this.isInCall) {
      if (this.phoneNumber.length > 0) {
        this.phoneNumber = this.phoneNumber.slice(0, -1);
        this.updateDisplay();
      }
      // If no phone number, just do nothing (button still responds to click)
    }
  }

  // Clear display
  clear() {
    if (!this.isInCall) {
      this.phoneNumber = "";
      this.updateDisplay();
    }
  }

  // Update display
  updateDisplay() {
    if (this.hasDisplayTarget) {
      this.displayTarget.textContent = this.phoneNumber || "Enter number";
    }
  }

  // Make call via server-side creation (same flow as the call form)
  async call() {
    // Check if organization has a phone number configured
    if (!this.hasPhoneNumberValue) {
      alert("No phone number configured. Please add a phone number to your organization before making calls.\n\nGo to Phone Numbers in the sidebar to provision or port a number.");
      return;
    }

    // Check subscription before allowing call
    if (!this.hasSubscriptionValue) {
      alert("You need to select a subscription plan to make calls. Please choose a plan to continue.");
      window.location.href = "/plans";
      return;
    }

    if (!this.canMakeCallValue) {
      alert("You have reached the limit for included minutes on your current plan. Please upgrade to continue making calls.");
      window.location.href = "/plans";
      return;
    }

    // Allow international numbers with + prefix (e.g., +34612345678)
    // Minimum 10 characters for US numbers, or 8+ digits with country code
    const digitsOnly = this.phoneNumber.replace(/\D/g, "");
    if (!this.phoneNumber || digitsOnly.length < 8) {
      alert("Please enter a valid phone number (at least 8 digits)");
      return;
    }

    this.updateStatus("Connecting...");
    this.updateCallButtons(true);

    // Get the current base path (e.g., "/bridgeinnovationsllc/en")
    const pathParts = window.location.pathname.split("/").filter(p => p);
    const basePath = pathParts.length >= 2 ? `/${pathParts[0]}/${pathParts[1]}` : "";

    try {
      const response = await fetch(`${basePath}/calls`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("[name=\"csrf-token\"]").content
        },
        body: JSON.stringify({ to_number: this.phoneNumber })
      });

      const data = await response.json();

      // Backend creates one call and dials the customer; we navigate to that call's active
      // page so the agent joins the same conference (call_<id>). Do not dial from the browser.
      if (response.ok && data.active_url) {
        Turbo.visit(data.active_url);
      } else {
        this.updateStatus("Call failed");
        this.updateCallButtons(false);
        alert(data.error || "Failed to initiate call. Please try again.");
      }
    } catch (error) {
      console.error("Failed to initiate call:", error);
      this.updateStatus("Call failed");
      this.updateCallButtons(false);
      alert("Failed to initiate call. Please try again.");
    }
  }

  // Get telephony client controller (Telnyx or Twilio)
  getClientController() {
    // Try Telnyx first, then Twilio
    let element = document.querySelector("[data-controller*='telnyx-client']");
    if (element) {
      try {
        const controller = this.application.getControllerForElementAndIdentifier(element, "telnyx-client");
        if (controller) {
          return { controller, provider: "telnyx" };
        }
      } catch (error) {
        console.warn("Failed to get Telnyx controller:", error);
      }
    }

    // Fallback to Twilio
    element = document.querySelector("[data-controller*='twilio-client']") ||
              document.querySelector("[data-controller~='twilio-client']");
    if (element) {
      try {
        const controller = this.application.getControllerForElementAndIdentifier(element, "twilio-client");
        if (controller) {
          return { controller, provider: "twilio" };
        }
      } catch (error) {
        console.warn("Failed to get Twilio controller:", error);
      }
    }

    console.error("No telephony client controller found (tried telnyx-client and twilio-client)");
    return null;
  }

  // Legacy method for backward compatibility
  getTwilioController() {
    const result = this.getClientController();
    return result?.controller || null;
  }

  // Hang up call
  hangup(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const result = this.getClientController();

    if (result?.controller) {
      const { controller } = result;
      if (typeof controller.hangUp === "function") {
        controller.hangUp();
      } else if (typeof controller.hangup === "function") {
        controller.hangup();
      }
    } else {
      // Fallback: try to disconnect via window reference
      if (window.currentTelnyxCall) {
        window.currentTelnyxCall.hangup();
        window.currentTelnyxCall = null;
      } else if (window.currentTwilioCall) {
        window.currentTwilioCall.disconnect();
        window.currentTwilioCall = null;
      }
    }

    this.isInCall = false;
    this.updateStatus("Call ended");
    this.updateCallButtons(false);

    // Clear display after a delay
    setTimeout(() => {
      this.phoneNumber = "";
      this.updateDisplay();
      this.updateStatus("");
    }, 2000);
  }

  // Toggle mute
  toggleMute(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const result = this.getClientController();

    if (result?.controller && typeof result.controller.toggleMute === "function") {
      this.isMuted = result.controller.toggleMute();
      this.updateMuteButton();
    } else {
      // Fallback: try to mute via window reference
      if (window.currentTelnyxCall) {
        if (this.isMuted) {
          window.currentTelnyxCall.unmuteAudio();
        } else {
          window.currentTelnyxCall.muteAudio();
        }
        this.isMuted = !this.isMuted;
        this.updateMuteButton();
      } else if (window.currentTwilioCall) {
        const isMuted = window.currentTwilioCall.isMuted();
        window.currentTwilioCall.mute(!isMuted);
        this.isMuted = !isMuted;
        this.updateMuteButton();
      } else {
        console.warn("No active call to mute/unmute");
      }
    }
  }

  updateMuteButton() {
    if (this.hasMuteButtonTarget) {
      if (this.isMuted) {
        this.muteButtonTarget.classList.add("bg-red-500", "text-white");
        this.muteButtonTarget.classList.remove("bg-gray-200", "text-gray-700", "dark:bg-gray-700", "dark:text-gray-300");
      } else {
        this.muteButtonTarget.classList.remove("bg-red-500", "text-white");
        this.muteButtonTarget.classList.add("bg-gray-200", "text-gray-700", "dark:bg-gray-700", "dark:text-gray-300");
      }
    }
  }

  // Send DTMF tone during call
  sendDTMF(digit) {
    const result = this.getClientController();

    if (result?.controller && typeof result.controller.sendDigit === "function") {
      result.controller.sendDigit(digit);
    } else if (result?.controller && typeof result.controller.sendDigits === "function") {
      result.controller.sendDigits(digit);
    } else if (window.currentTelnyxCall && typeof window.currentTelnyxCall.dtmf === "function") {
      // Telnyx uses dtmf() method
      window.currentTelnyxCall.dtmf(digit);
    } else if (window.currentTwilioCall) {
      // Twilio uses sendDigits() method
      window.currentTwilioCall.sendDigits(digit);
    }
  }

  // Update call buttons visibility
  updateCallButtons(inCall) {
    if (this.hasCallButtonTarget) {
      this.callButtonTarget.classList.toggle("hidden", inCall);
    }
    if (this.hasHangupButtonTarget) {
      this.hangupButtonTarget.classList.toggle("hidden", !inCall);
    }
    if (this.hasMuteButtonTarget) {
      this.muteButtonTarget.classList.toggle("hidden", !inCall);
    }
  }

  // Update status message
  updateStatus(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message;
      this.statusTarget.classList.toggle("hidden", !message);
    }
  }

  // Event handlers for Twilio call events
  handleRinging(_event) {
    this.isInCall = true;
    this.updateStatus("Ringing...");
    this.updateCallButtons(true);
  }

  handleAccepted(_event) {
    this.isInCall = true;
    this.updateStatus("Connected");
    this.updateCallButtons(true);
  }

  handleDisconnected(_event) {
    this.isInCall = false;
    this.isMuted = false;
    this.updateStatus("Call ended");
    this.updateCallButtons(false);

    setTimeout(() => {
      this.phoneNumber = "";
      this.updateDisplay();
      this.updateStatus("");
    }, 2000);
  }

  handleError(event) {
    this.isInCall = false;
    this.isMuted = false;
    this.updateStatus("Call failed");
    this.updateCallButtons(false);
    alert("Call failed: " + (event.detail?.error?.message || event.detail?.error || "Unknown error"));

    setTimeout(() => {
      this.updateStatus("");
    }, 3000);
  }

  // Play DTMF tone (optional)
  playTone(digit) {
    // DTMF frequencies mapping
    const tones = {
      "1": [697, 1209], "2": [697, 1336], "3": [697, 1477],
      "4": [770, 1209], "5": [770, 1336], "6": [770, 1477],
      "7": [852, 1209], "8": [852, 1336], "9": [852, 1477],
      "*": [941, 1209], "0": [941, 1336], "#": [941, 1477]
    };

    if (!tones[digit]) return;

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const [freq1, freq2] = tones[digit];

      const oscillator1 = audioContext.createOscillator();
      const oscillator2 = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator1.frequency.value = freq1;
      oscillator2.frequency.value = freq2;
      gainNode.gain.value = 0.1;

      oscillator1.connect(gainNode);
      oscillator2.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator1.start();
      oscillator2.start();

      setTimeout(() => {
        oscillator1.stop();
        oscillator2.stop();
      }, 100);
    } catch (error) {
      // Ignore audio errors
      console.warn("Could not play DTMF tone:", error);
    }
  }
}
